import React, { ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";

export type ConfirmActionDialogProps = {
  /** Whether the dialog is open */
  open: boolean;
  /** The label/title for the action being confirmed */
  actionLabel: string;
  /** Description text or custom content to display in the dialog body */
  description: string | ReactNode;
  /** Text for the confirm button (default: "Confirm") */
  confirmText?: string;
  /** Text for the cancel button (default: "Cancel") */
  cancelText?: string;
  /** Whether the confirm action is in progress */
  loading?: boolean;
  /** Callback when confirm button is clicked */
  onConfirm: () => void;
  /** Callback when cancel button is clicked or dialog is dismissed */
  onCancel: () => void;
};

/**
 * A reusable confirmation dialog component for action confirmations.
 * Shows a title, description, and Confirm/Cancel buttons.
 * Supports loading state to prevent double-submits.
 */
export const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  open,
  actionLabel,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  loading = false,
  onConfirm,
  onCancel,
}) => (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      aria-labelledby="confirm-action-dialog-title"
      aria-describedby="confirm-action-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="confirm-action-dialog-title">{actionLabel}</DialogTitle>
      <DialogContent>
        {typeof description === "string" ? (
          <DialogContentText id="confirm-action-dialog-description">
            {description}
          </DialogContentText>
        ) : (
          <Box id="confirm-action-dialog-description">{description}</Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          disabled={loading}
          sx={{
            color: "#041E42",
            borderColor: "#041E42",
            "&:hover": {
              borderColor: "#0a3d7a",
              backgroundColor: "rgba(4, 30, 66, 0.04)",
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : null
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? "Processing..." : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );

export default ConfirmActionDialog;
